import { AgGridReact } from 'ag-grid-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import 'ag-grid-enterprise'

import { useQuery } from '@tanstack/react-query'

import { buildAlert } from '../../actions'
import { postContractComplete } from '../../actions/contracts/contractcompletepost'
//import { deleteContract } from '../../actions/contracts/contractdelete'
import { postContract } from '../../actions/contracts/contractpost'
import { postContractReopen } from '../../actions/contracts/contractreopenpost'
import { getAllContracts } from '../../actions/contracts/contractsgetall'
//import { getAllContractsModified } from '../../actions/contracts/contractsgetallmodified'
import { getPersonContracts } from '../../actions/contracts/contractspersonget'
import { addGridData, getLocaleText, removeGridData, resetSorting, updateGridData } from '../../actions/datagrid'
import { checkPersonNumber } from '../../actions/persons/personnumbercheck'
import { currentUserPersonQuery } from '../../api/index.js'
import { Button } from '../../common/Button.tsx'
import {
	contextMenu,
	defaultColDef,
	gridSettings,
	loadViewState,
	restoreViewState,
	saveViewState,
	sideBar,
	statusBar,
} from '../../common/grid'
import {
	CheckCircleIcon,
	CircleIcon,
	filesIconString,
	IconAddNew,
	magnifierIconString,
	pencilIconString,
	printerIconString,
	restoreIconString,
} from '../../common/icons'
import LoadingCentered from '../../common/loaders/loadingcentered'
import { PdfReport } from '../../common/pdfreport'
import { dateFormatter, dateGetter, numberFormatterFraction /*useInterval*/ } from '../../common/utils'
import {
	actionTypeSelector,
	authSelector,
	contractSelector,
	contractsModifiedSelector,
	contractsSelector,
	personNumberCheckSelector,
} from '../../reducers'
import NewContractModal from './modal'
import CopyContractModal from './modal/copycontractmodal'
import EditContractModal from './modal/editcontractmodal'
import { PdfModal } from './pdfmodal'

const viewStateKey = 'contractViewState'
const identifierRE = /([0-9]+)/

/* https://phantasos.heeros.com:8025/TyoSopimusTaulu */

const initialState = {
	columnVisibility: {
		hiddenColIds: ['details.startDate', 'person.startDate', 'details.jobTitle', 'pay.amount'],
	},
}

export default function ContractView({ isPersonal }) {
	//const { contractIdentifier } = useParams(); // open contract from path /mgmt/contracts/:contractIdentifier
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const checkPersonNumberCB = useCallback((nr) => dispatch(checkPersonNumber(nr)))
	const showFormErrorsAlert = useCallback((title, description, errors) =>
		dispatch(buildAlert('ERROR', title, description, Object.values(errors))),
	)
	const lockContract = useCallback((contractId) => dispatch(postContractComplete(contractId)))
	const unlockContract = useCallback((contractId) => dispatch(postContractReopen(contractId)))

	const auth = useSelector(authSelector)
	const actionType = useSelector(actionTypeSelector)
	const contracts = useSelector(contractsSelector)

	const contract = useSelector(contractSelector)
	const personNumberCheckResult = useSelector(personNumberCheckSelector)

	const contractsModified = useSelector(contractsModifiedSelector)

	const [newContract, setNewContract] = useState(null)
	const [edit, setEdit] = useState(null)
	const [copy, setCopy] = useState(null)
	const [pdf, setPdf] = useState(null)

	const [showLoading, setShowLoading] = useState(false)
	const [alertPayload, setAlertPayload] = useState(null)

	const [report, setReport] = useState(null)
	const closeReport = () => setReport(null)

	//useEffect(() => {
	//  if (person?.personId) {
	//    dispatch(getPersonContracts(person?.personId));
	//  } else {
	//    dispatch(getAllContracts());
	//  }
	//return () => dispatch({type:"CONTRACTS_UNMOUNT", payload: { contracts: [] }});
	// }, []);

	const person = useQuery(currentUserPersonQuery(isPersonal)).data

	const closeModal = useCallback(() => {
		setNewContract(null)
		setEdit(null)
		setCopy(null)
	}, [])

	useEffect(() => {
		if (isPersonal && person?.personId) {
			dispatch(getPersonContracts(person.personId))
		} else if (!isPersonal) {
			dispatch(getAllContracts())
		}
	}, [isPersonal, person?.personId, dispatch])
	/*
	const fetchModifiedContracts = useCallback((modifiedSince) => dispatch(getAllContractsModified(modifiedSince)))
	const [modifiedSince, setModifiedSince] = useState(Date.now())
	// Poll modified contracts every 30 seconds
	if (import.meta.env.TEST) {
		// production
		useInterval(() => {
			const tmpModifiedSince = modifiedSince
			setModifiedSince(Date.now())
			fetchModifiedContracts(tmpModifiedSince)
		}, 30 * 1000)
	}
*/
	useEffect(() => {
		if (gridRef.current.api) {
			if (contractsModified?.newContracts?.length > 0) {
				addGridData(gridRef, contractsModified.newContracts, 0)
			}
			if (contractsModified?.modifiedContracts?.length > 0) {
				updateGridData(gridRef, contractsModified.modifiedContracts)
			}
			if (contractsModified?.deletedContracts?.length > 0) {
				removeGridData(gridRef, contractsModified.deletedContracts)
			}
		}
	}, [contractsModified])

	useEffect(() => {
		if (
			actionType === 'POST_CONTRACT_LOADING' ||
			actionType === 'POST_CONTRACT_EDIT_LOADING' ||
			actionType === 'POST_CONTRACT_COMPLETE_LOADING' ||
			actionType === 'POST_CONTRACT_REOPEN_LOADING'
		) {
			// Let contractmodalbody handle these
		} else if (actionType.endsWith('_LOADING')) {
			setShowLoading(true)
		}

		if (actionType === 'POST_CONTRACT_SUCCEEDED') {
			resetSorting(gridRef)
			addGridData(gridRef, [contract], 0)
		} else if (
			actionType === 'POST_CONTRACT_EDIT_SUCCEEDED' ||
			actionType === 'POST_CONTRACT_COMPLETE_SUCCEEDED' ||
			actionType === 'POST_CONTRACT_REOPEN_SUCCEEDED'
		) {
			updateGridData(gridRef, [contract])
		} else if (actionType.endsWith('_SUCCEEDED') || actionType.endsWith('_DONE')) {
			setShowLoading(false)
		}

		if (
			actionType === 'UPLOAD_DONE' ||
			actionType === 'POST_CONTRACT_COMPLETE_SUCCEEDED' ||
			actionType === 'POST_CONTRACT_REOPEN_SUCCEEDED'
		) {
			closeModal()
		}

		if (actionType === 'DELETE_CONTRACT_SUCCEEDED') {
			removeGridData(gridRef, [contract])
		}

		if (actionType.endsWith('_FAIL')) {
			setShowLoading(false)
		}

		if (actionType === 'GET_PERSON_CONTRACTS_SUCCEEDED') {
			setShowLoading(false)
		}
	}, [actionType, contract, closeModal])

	useEffect(() => {
		if (alertPayload != null) {
			dispatch(alertPayload)
			setAlertPayload(null)
		}
	}, [alertPayload, dispatch])

	function onSave(contract) {
		dispatch(
			postContract({
				contract: contract,
				newPerson: contract.newPerson,
				newModel: contract.modelName && contract.modelName.trim().length > 0,
			}),
		)
	}

	function onFormErrors(errors) {
		showFormErrorsAlert(
			t('FormContainsErrors', 'Lomakkeella on virheitä'),
			t('PleaseFixFormErrors', 'Ole hyvä ja korjaa virheet'),
			Object.values(errors),
		)
	}
	/*
	const removeContract = (contractId) => {
		if (confirm(t('DeleteContractConfirm', 'Haluatko varmasti poistaa työsopimuksen?'))) {
			dispatch(deleteContract(contractId))
		}
	}
*/
	const gridRef = useRef()

	const contractStatusValueFormatter = (params) => {
		return t(`ContractStatus_${params.value}`, params.value)
	}

	const ContractStatusRenderer = (params) => {
		let statusIcon
		if (params.value === 'DRAFT' || params.value === 'REOPENED' || params.value === 'MODEL') {
			statusIcon = <CircleIcon />
		} else if (params.value === 'COMPLETE') {
			statusIcon = <CheckCircleIcon />
		}

		const statusTooltip = <Tooltip id="tooltip-right">{contractStatusValueFormatter(params)}</Tooltip>

		return (
			<OverlayTrigger placement="right" overlay={statusTooltip}>
				<span key={params.data?.contractId}>{statusIcon}</span>
			</OverlayTrigger>
		)
	}

	const gridComponents = {
		contractStatusRenderer: ContractStatusRenderer,
	}

	const identifierComparator = (valueA, valueB) => {
		const splitA = valueA.split(identifierRE).filter(Boolean)
		const splitB = valueB.split(identifierRE).filter(Boolean)

		for (let i = 0; i < Math.min(splitA.length, splitB.length); i++) {
			const segmentA = splitA[i]
			const segmentB = splitB[i]
			const isNumberA = !Number.isNaN(Number(segmentA))
			const isNumberB = !Number.isNaN(Number(segmentB))

			if (isNumberA && isNumberB) {
				const numA = parseInt(segmentA, 10)
				const numB = parseInt(segmentB, 10)
				if (numA !== numB) {
					return numA - numB
				}
			} else {
				const comparisonResult = segmentA.localeCompare(segmentB)
				if (comparisonResult !== 0) {
					return comparisonResult
				}
			}
		}

		return splitA.length - splitB.length
	}

	const columnDefs = [
		{
			headerName: t('Status', 'Tila'),
			field: 'status',
			suppressSizeToFit: true,
			width: 125,
			filter: 'agSetColumnFilter',
			filterParams: {
				values: ['DRAFT', 'MODEL', 'COMPLETE'],
				valueFormatter: contractStatusValueFormatter,
			},
			cellRenderer: 'contractStatusRenderer',
		},
		{
			headerName: t('Created', 'Luotu'),
			field: 'date',
			cellClass: 'dateType',
			filter: 'agDateColumnFilter',
			valueGetter: dateGetter,
			valueFormatter: dateFormatter,
		},
		{
			headerName: t('Identifier', 'Tunnus'),
			field: 'identifier',
			sortable: true,
			comparator: identifierComparator,
		},
		{
			headerName: t('Name', 'Nimi'),
			field: 'person.name',
		},
		{
			headerName: t('ModelName', 'Mallin nimi'),
			field: 'modelName',
		},
		{
			headerName: t('NewContractStartDate', 'Uuden työsopimuksen alkupvm.'),
			field: 'details.startDate',
			filter: 'agDateColumnFilter',
			suppressSizeToFit: true,
			hide: isPersonal,
			valueGetter: dateGetter,
			valueFormatter: dateFormatter,
		},
		{
			headerName: t('ValidityPeriod', 'Voimassaoloaika'),
			field: 'details.endDate',
			filter: 'agDateColumnFilter',
			suppressSizeToFit: true,
			hide: isPersonal,
			valueGetter: dateGetter,
			valueFormatter: dateFormatter,
		},
		{
			headerName: t('StartDate', 'Työsuhteen aloituspäivä'),
			field: 'person.startDate',
			filter: 'agDateColumnFilter',
			suppressSizeToFit: true,
			hide: isPersonal,
			valueGetter: dateGetter,
			valueFormatter: dateFormatter,
		},
		{
			headerName: t('TrialPeriodEndDate', 'Koeaika päättyy'),
			field: 'details.trialPeriodEndDate',
			filter: 'agDateColumnFilter',
			suppressSizeToFit: true,
			hide: isPersonal,
			valueGetter: dateGetter,
			valueFormatter: dateFormatter,
		},
		{
			headerName: t('JobTitle', 'Tehtävänimike'),
			field: 'details.jobTitle',
		},
		{
			headerName: t('PayAmount', 'Palkka'),
			field: 'pay.amount',
			filter: 'agNumberColumnFilter',
			valueFormatter: numberFormatterFraction,
		},
	]

	const getContextMenuItems = useCallback(
		(params) => {
			let items = []

			items.push({
				name: t('DefaultGridValues', 'Palauta taulukon oletusasetukset'),
				action: () => {
					restoreViewState(viewStateKey, initialState)
				},
				icon: restoreIconString,
			})

			items.push('separator')

			if (!params.api.isPivotMode()) {
				const lockedContract = params.node?.data?.status === 'COMPLETE'

				if (lockedContract) {
					items.push({
						name: t('ViewContract', 'Tarkastele sopimusta'),
						action: () => {
							setEdit(params.node?.data)
						},
						icon: magnifierIconString,
					})
				} else {
					items.push({
						name: t('ModifyContract', 'Muokkaa sopimusta'),
						action: () => {
							setEdit(params.node?.data)
						},
						icon: pencilIconString,
					})
				}

				items.push({
					name: t('CopyContract', 'Kopioi sopimus uuden pohjaksi'),
					action: () => {
						setCopy(params.node?.data)
					},
					icon: filesIconString,
				})
				/*
            if (auth.admin && !lockedContract) {
              items.push(
                {
                  name: t("DeleteContract", "Poista sopimus"),
                  action: function () {
                    removeContract(params.node?.id);
                  },
                  icon: trashIconString
                },
              );
            }
      */
				items.push(
					{
						//          name: params.node?.data.status === "COMPLETE" ? t("PrintContract", "Tulosta sopimus") : t("Preview", "Esikatselu"),
						name: t('PrintContract', 'Tulosta sopimus'),
						action: () => setPdf({ ...params.node?.data, status: 'COMPLETE' }), // No draft watermark
						icon: printerIconString,
					},
					'separator',
				)
			}
			items = items.concat(
				contextMenu(t, params, setReport, {
					title: t('Contracts', 'Työsopimukset'),
					companyname: auth.companyName,
				}),
			)
			return items
		},
		[t, auth.companyName],
	)

	const doubleClickAction = (row) => setEdit(row.data)

	return (
		<>
			<LoadingCentered show={showLoading} />
			{report !== null && <PdfReport json={report} onCloseReport={closeReport} /> /* PDF report of grid contents */}
			{pdf !== null && <PdfModal contract={pdf} onCancel={closeModal} setPdf={setPdf} /> /* Contract PDF */}

			{newContract !== null && (
				<NewContractModal
					contract={newContract}
					onCancel={closeModal}
					onSave={onSave}
					checkPersonNumber={checkPersonNumberCB}
					personNumberCheckResult={personNumberCheckResult}
					actionType={actionType}
					onFormErrors={onFormErrors}
					setPdf={setPdf}
				/>
			)}

			{edit !== null && (
				<EditContractModal
					contract={edit}
					setContract={setEdit}
					onCancel={closeModal}
					onSave={onSave}
					onLock={lockContract}
					onUnlock={unlockContract}
					checkPersonNumber={checkPersonNumberCB}
					personNumberCheckResult={personNumberCheckResult}
					actionType={actionType}
					onFormErrors={onFormErrors}
					setPdf={setPdf}
				/>
			)}

			{copy !== null && (
				<CopyContractModal
					contract={copy}
					setContract={setCopy}
					onCancel={closeModal}
					onSave={onSave}
					checkPersonNumber={checkPersonNumberCB}
					personNumberCheckResult={personNumberCheckResult}
					actionType={actionType}
					onFormErrors={onFormErrors}
					setPdf={setPdf}
				/>
			)}

			<Row className="my-1">
				<Col sm="4">
					<h2 className="viewheading">{t('Contracts', 'Työsopimukset')}</h2>
				</Col>
				{!auth.personalProfile && !isPersonal && (
					<Col sm="8" align="right">
						<Row className="g-2">
							<Col></Col>
							<Col md="auto">
								<Button variant="primary" className="addbutton" onClick={() => setNewContract({})}>
									<IconAddNew /> {t('AddContract', 'Lisää työsopimus')}
								</Button>
							</Col>
						</Row>
					</Col>
				)}
			</Row>

			<Row className="ag-theme-material styles-ag flex-grow-1 mt-3">
				<AgGridReact
					{...gridSettings}
					ref={gridRef}
					components={gridComponents}
					defaultColDef={defaultColDef}
					columnDefs={columnDefs}
					sideBar={sideBar}
					rowData={contracts ?? []}
					getContextMenuItems={getContextMenuItems}
					statusBar={statusBar}
					onRowDoubleClicked={doubleClickAction}
					getRowId={(params) => String(params.data?.contractId)}
					getLocaleText={(params) => getLocaleText(t, params)}
					animateRows
					suppressCellFocus
					enableCellTextSelection
					initialState={loadViewState(viewStateKey) || initialState}
					onStateUpdated={({ state }) => saveViewState(state, viewStateKey)}
					debug={import.meta.env.DEV}
				/>
			</Row>
		</>
	)
}
